/**
 * useAmbientCatalog (admin) — fetches the admin-managed catalogue
 * from the public `/api/admin/ambient-effects/active` endpoint and
 * pushes the rows into the admin module-level LIVE_EFFECTS cache so
 * the existing helpers (getAmbientEffect, getAmbientEffectsForSlide)
 * pick up the latest codes without rewriting every consumer.
 */
import { useEffect } from 'react';
import type { SlideType } from './effects-catalogue';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001/api';

export interface AmbientEffectMeta {
  id: string;
  code: string;
  label: string;
  hint: string | null;
  accent: string | null;
  tier: 'FREE' | 'PRO';
  freeTierEnabled: boolean;
  renderer: 'canvas' | 'css' | 'css-static';
  rendererKey: string | null;
  slideTypes: string[] | null;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

const COMPLEMENT: Record<string, { renderer: AmbientEffectMeta['renderer']; rendererKey: string | null }> = {
  none: { renderer: 'canvas', rendererKey: 'none' },
  'ambient-orbs': { renderer: 'css-static', rendererKey: 'ambient-orbs' },
  'floating-heart-bokeh': { renderer: 'canvas', rendererKey: 'floating-heart-bokeh' },
  'velvet-dust-motes': { renderer: 'canvas', rendererKey: 'velvet-dust-motes' },
  'firefly-courtship': { renderer: 'canvas', rendererKey: 'firefly-courtship' },
  'champagne-bubble-rise': { renderer: 'canvas', rendererKey: 'champagne-bubble-rise' },
  'rose-petal-cascade': { renderer: 'canvas', rendererKey: 'rose-petal-cascade' },
  'sparkler-trail-hearts': { renderer: 'canvas', rendererKey: 'sparkler-trail-hearts' },
  'constellation-connection': { renderer: 'canvas', rendererKey: 'constellation-connection' },
  'heartbeat-pulse-rings': { renderer: 'canvas', rendererKey: 'heartbeat-pulse-rings' },
  'silk-ribbon-flow': { renderer: 'canvas', rendererKey: 'silk-ribbon-flow' },
  'diamond-dust-sparkle': { renderer: 'canvas', rendererKey: 'diamond-dust-sparkle' },
  'pheromone-trail': { renderer: 'canvas', rendererKey: 'pheromone-trail' },
  'aurora-heart': { renderer: 'canvas', rendererKey: 'aurora-heart' },
  'floating-kiss-marks': { renderer: 'canvas', rendererKey: 'floating-kiss-marks' },
  'memory-fragment-drift': { renderer: 'canvas', rendererKey: 'memory-fragment-drift' },
  'entwined-particle-vines': { renderer: 'canvas', rendererKey: 'entwined-particle-vines' },
};

export function useAmbientCatalog(): { effects: AmbientEffectMeta[]; loading: boolean; error: string | null } {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/ambient-effects/active`);
        if (!res.ok) return;
        const data = await res.json();
        const rows: AmbientEffectMeta[] = Array.isArray(data?.effects) ? data.effects : [];
        if (cancelled) return;
        const enriched = rows
          .map((row) => {
            const cmp = COMPLEMENT[row.code];
            if (cmp) return { ...row, renderer: cmp.renderer, rendererKey: cmp.rendererKey };
            return { ...row, renderer: row.renderer || 'css', rendererKey: row.rendererKey ?? row.code };
          })
          .sort((a, b) => a.order - b.order);
        const mod = await import('./effects-catalogue');
        mod.setLiveAmbientEffects(enriched.map((row) => ({
          code: row.code,
          label: row.label,
          tier: row.tier,
          hint: row.hint ?? row.code,
          slideTypes: (row.slideTypes ?? ['TITLE', 'MESSAGE', 'PHOTO', 'MEMORY', 'QUESTION', 'FINAL']) as SlideType[],
          order: row.order,
          accent: row.accent ?? '#ffffff',
        })));
      } catch {
        // Silent — the hard-coded fallback covers offline preview use.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { effects: [], loading: false, error: null };
}
